import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Plus, 
  Package,
  Pencil,
  Trash2,
  Search,
  Filter
} from 'lucide-react';
import { toast } from 'sonner';

const CATEGORIES = [
  { value: 'dairy', label: 'Dairy' },
  { value: 'meat', label: 'Meat' },
  { value: 'seafood', label: 'Seafood' },
  { value: 'produce', label: 'Produce' },
  { value: 'dry_goods', label: 'Dry Goods' },
  { value: 'beverages', label: 'Beverages' },
  { value: 'bakery', label: 'Bakery' },
  { value: 'frozen', label: 'Frozen' },
  { value: 'condiments', label: 'Condiments' },
  { value: 'other', label: 'Other' },
];

const UNITS = [
  { value: 'kg', label: 'kg' },
  { value: 'g', label: 'g' },
  { value: 'L', label: 'L' },
  { value: 'ml', label: 'ml' },
  { value: 'each', label: 'each' },
  { value: 'portion', label: 'portion' },
  { value: 'bunch', label: 'bunch' },
  { value: 'can', label: 'can' },
  { value: 'bottle', label: 'bottle' },
  { value: 'loaf', label: 'loaf' },
  { value: 'pack', label: 'pack' },
];

const IngredientsPage = () => {
  const { currentLocation, api } = useAuth();
  const [ingredients, setIngredients] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingIngredient, setEditingIngredient] = useState(null);
  const [deleteIngredient, setDeleteIngredient] = useState(null);
  const [form, setForm] = useState({
    name: '',
    category: 'other',
    unit: 'each',
    cost_per_unit: '',
    supplier_id: '',
    notes: ''
  });

  useEffect(() => {
    if (currentLocation) {
      fetchIngredients();
      fetchSuppliers();
    }
  }, [currentLocation]);

  const fetchIngredients = async () => {
    setLoading(true);
    try {
      const res = await api.get('/ingredients', { params: { location_id: currentLocation.id } });
      setIngredients(res.data);
    } catch (err) {
      console.error('Error fetching ingredients:', err);
      toast.error('Failed to load ingredients');
    } finally {
      setLoading(false);
    }
  };

  const fetchSuppliers = async () => {
    try {
      const res = await api.get('/suppliers', { params: { location_id: currentLocation.id } });
      setSuppliers(res.data);
    } catch (err) {
      console.error('Error fetching suppliers:', err);
    }
  };

  const handleOpenDialog = (ingredient = null) => {
    if (ingredient) {
      setEditingIngredient(ingredient);
      setForm({
        name: ingredient.name,
        category: ingredient.category,
        unit: ingredient.unit,
        cost_per_unit: ingredient.cost_per_unit?.toString() || '',
        supplier_id: ingredient.supplier_id || '',
        notes: ingredient.notes || ''
      });
    } else {
      setEditingIngredient(null);
      setForm({
        name: '',
        category: 'other',
        unit: 'each',
        cost_per_unit: '',
        supplier_id: '',
        notes: ''
      });
    }
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error('Please enter an ingredient name');
      return;
    }

    try {
      const data = {
        ...form,
        location_id: currentLocation.id,
        cost_per_unit: parseFloat(form.cost_per_unit) || 0,
        supplier_id: form.supplier_id || null
      };

      if (editingIngredient) {
        await api.put(`/ingredients/${editingIngredient.id}`, data);
        toast.success('Ingredient updated');
      } else {
        await api.post('/ingredients', data);
        toast.success('Ingredient created');
      }
      
      setDialogOpen(false);
      fetchIngredients();
    } catch (err) {
      console.error('Error saving ingredient:', err);
      toast.error('Failed to save ingredient');
    }
  };

  const handleDelete = async () => {
    if (!deleteIngredient) return;
    try {
      await api.delete(`/ingredients/${deleteIngredient.id}`);
      toast.success('Ingredient deleted');
      setDeleteIngredient(null);
      fetchIngredients();
    } catch (err) {
      console.error('Error deleting ingredient:', err);
      toast.error(err.response?.data?.detail || 'Failed to delete ingredient');
    }
  };

  const formatCurrency = (val) => `£${(val || 0).toFixed(2)}`;

  const getCategoryBadge = (category) => {
    const colors = {
      dairy: 'bg-blue-100 text-blue-800',
      meat: 'bg-red-100 text-red-800',
      seafood: 'bg-cyan-100 text-cyan-800',
      produce: 'bg-green-100 text-green-800',
      dry_goods: 'bg-amber-100 text-amber-800',
      beverages: 'bg-purple-100 text-purple-800',
      bakery: 'bg-orange-100 text-orange-800',
      frozen: 'bg-sky-100 text-sky-800',
      condiments: 'bg-yellow-100 text-yellow-800',
      other: 'bg-gray-100 text-gray-800',
    };
    const label = CATEGORIES.find(c => c.value === category)?.label || category;
    return <Badge className={colors[category] || colors.other}>{label}</Badge>;
  };

  // Filter ingredients
  const filteredIngredients = ingredients.filter(ing => {
    const matchesSearch = ing.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || ing.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  // Stats
  const totalIngredients = ingredients.length;
  const avgCost = ingredients.length > 0 
    ? ingredients.reduce((sum, i) => sum + (i.cost_per_unit || 0), 0) / ingredients.length 
    : 0;

  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-24" />)}
        </div>
        <Skeleton className="h-96" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Ingredients</h1>
          <p className="text-muted-foreground">Manage your ingredient library and costs</p>
        </div>
        <Button onClick={() => handleOpenDialog()} data-testid="add-ingredient-btn">
          <Plus className="w-4 h-4 mr-2" />
          Add Ingredient
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Total Ingredients</CardDescription>
            <CardTitle className="text-2xl">{totalIngredients}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Categories Used</CardDescription>
            <CardTitle className="text-2xl">{new Set(ingredients.map(i => i.category)).size}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>Avg Cost/Unit</CardDescription>
            <CardTitle className="text-2xl">{formatCurrency(avgCost)}</CardTitle>
          </CardHeader>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardDescription>With Suppliers</CardDescription>
            <CardTitle className="text-2xl">{ingredients.filter(i => i.supplier_id).length}</CardTitle>
          </CardHeader>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search ingredients..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="search-ingredients"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-[180px]">
            <Filter className="w-4 h-4 mr-2" />
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {filteredIngredients.length === 0 ? (
            <div className="py-12 text-center">
              <Package className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="font-semibold text-lg mb-2">
                {ingredients.length === 0 ? 'No ingredients yet' : 'No matching ingredients'}
              </h3>
              <p className="text-muted-foreground mb-4">
                {ingredients.length === 0 
                  ? 'Start building your ingredient library' 
                  : 'Try adjusting your search or filters'}
              </p>
              {ingredients.length === 0 && (
                <Button onClick={() => handleOpenDialog()}>
                  <Plus className="w-4 h-4 mr-2" />
                  Add First Ingredient
                </Button>
              )}
            </div>
          ) : (
            <>
              {/* Mobile Card View */}
              <div className="md:hidden space-y-3">
                {filteredIngredients.map((ingredient) => (
                  <div 
                    key={ingredient.id}
                    className="border rounded-lg p-4 space-y-2"
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium">{ingredient.name}</p>
                        <p className="text-sm text-muted-foreground">{ingredient.supplier_name || 'No supplier'}</p>
                      </div>
                      <span className="text-lg font-bold font-mono">{formatCurrency(ingredient.cost_per_unit)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {getCategoryBadge(ingredient.category)}
                        <span className="text-sm text-muted-foreground">per {ingredient.unit}</span>
                      </div>
                      <div className="flex gap-1">
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => handleOpenDialog(ingredient)}
                        >
                          <Pencil className="w-4 h-4" />
                        </Button>
                        <Button 
                          size="icon" 
                          variant="ghost"
                          onClick={() => setDeleteIngredient(ingredient)}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Unit</TableHead>
                      <TableHead className="text-right">Cost/Unit</TableHead>
                      <TableHead>Supplier</TableHead>
                      <TableHead className="w-[100px]">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredIngredients.map((ingredient) => (
                      <TableRow key={ingredient.id} className="table-row-hover">
                        <TableCell className="font-medium">{ingredient.name}</TableCell>
                        <TableCell>{getCategoryBadge(ingredient.category)}</TableCell>
                        <TableCell>{ingredient.unit}</TableCell>
                        <TableCell className="text-right font-mono">
                          {formatCurrency(ingredient.cost_per_unit)}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {ingredient.supplier_name || '-'}
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-1">
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => handleOpenDialog(ingredient)}
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button 
                              size="icon" 
                              variant="ghost"
                              onClick={() => setDeleteIngredient(ingredient)}
                              className="text-destructive hover:text-destructive"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingIngredient ? 'Edit Ingredient' : 'Add Ingredient'}</DialogTitle>
            <DialogDescription>
              {editingIngredient ? 'Update ingredient details' : 'Add a new ingredient to your library'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input
                value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                placeholder="e.g., Whole Milk"
                data-testid="ingredient-name-input"
              />
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={form.category} onValueChange={(val) => setForm({...form, category: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(cat => (
                      <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Unit</Label>
                <Select value={form.unit} onValueChange={(val) => setForm({...form, unit: val})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {UNITS.map(u => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cost per Unit (£)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cost_per_unit}
                  onChange={(e) => setForm({...form, cost_per_unit: e.target.value})}
                  placeholder="0.00"
                  data-testid="ingredient-cost-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label>Supplier</Label>
                <Select 
                  value={form.supplier_id || "none"} 
                  onValueChange={(val) => setForm({...form, supplier_id: val === "none" ? "" : val})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select supplier" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {suppliers.map(s => (
                      <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label>Notes</Label>
              <Input
                value={form.notes}
                onChange={(e) => setForm({...form, notes: e.target.value})}
                placeholder="Optional notes..."
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSave} data-testid="save-ingredient-btn">
              {editingIngredient ? 'Update' : 'Add'} Ingredient
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteIngredient} onOpenChange={() => setDeleteIngredient(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Ingredient</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{deleteIngredient?.name}&quot;? 
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default IngredientsPage;
